import React, { FC, useState, useEffect, PropsWithChildren, useContext } from 'react';
import classNames from 'classnames';
import View from '../view';
import Icon from '../icon';
import Group, { GroupContext } from './Group';

interface ICheckboxProps {
  className?: string;
  defaultChecked?: boolean;
  checked?: boolean;
  value?: string;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}

const BasicCheckbox: FC<PropsWithChildren<Omit<ICheckboxProps, 'value'>>> = ({ children, className = '', defaultChecked = false, disabled, checked: propsChecked, onChange }) => {
  const [checked, setChecked] = useState<boolean>(typeof propsChecked !== 'undefined' ? propsChecked : defaultChecked);
  const rootCls = classNames(
    'ink-checkbox',
    {
      'ink-checkbox--disabled': disabled,
      'ink-checkbox--selected': checked,
    },
    className,
  );
  const contentCls = classNames('ink-checkbox__label');

  const onClick = () => {
    if (disabled) {
      return;
    }
    /**
     * props.checked 传入，为受控组件，通过 onChange 事件来更新 checked
     * props.checked 未传入，未非受控组件，使用内部状态更新，但仍调用 onChange 回调
     */
    if (typeof propsChecked === 'undefined') {
      setChecked(!checked);
    }
    if (onChange && typeof onChange === 'function') {
      onChange(!checked);
    }
  };

  useEffect(() => {
    if (typeof propsChecked !== 'undefined') {
      setChecked(propsChecked);
    }
  }, [propsChecked]);

  return (
    <View className={rootCls} onClick={onClick}>
      <View className="ink-checkbox__icon-cnt">{checked && <Icon name="check" className="ink-checkbox__icon" />}</View>
      <View className={contentCls}>{children}</View>
    </View>
  );
};

const Checkbox: FC<PropsWithChildren<ICheckboxProps>> & { Group: typeof Group } = ({ value, ...restProps }) => {
  const groupContext = useContext(GroupContext);

  const checkboxProps = { ...restProps };

  if (groupContext) {
    const { value: groupValue = [], toggleOption, isControlled, disabled: groupDisabled } = groupContext;
    const isChecked = !!(value && groupValue?.includes(value));
    // isControlled 代表受控组件
    if (isControlled) {
      checkboxProps.checked = isChecked;
    } else {
      checkboxProps.defaultChecked = isChecked;
    }
    if (typeof restProps.disabled === 'undefined') {
      checkboxProps.disabled = groupDisabled;
    }
    checkboxProps.onChange = (changedChecked: boolean) => {
      if (restProps.onChange && typeof restProps.onChange === 'function') {
        restProps.onChange(changedChecked);
      }
      if (toggleOption) {
        toggleOption({ value, checked: changedChecked });
      }
    };
  }

  return <BasicCheckbox {...checkboxProps} />;
};

Checkbox.Group = Group;

export default Checkbox;
